<template>
    <div
        class="collapsible-facet"
    >
        <button
            type="button"
            class="facet-header"
            :aria-expanded="!isCollapsed"
            :aria-controls="`facet-body-${facetName}`"
            @click="toggleCollapse"
            @keydown.enter.prevent="toggleCollapse"
            @keydown.space.prevent="toggleCollapse"
            @keydown.escape="collapseSection"
        >
            <al-icon
                class="chevron-icon"
                :icon="`${isCollapsed ? 'chevron-right' : 'chevron-down'}`"
                :test-id="`collapseIcon-${facetName}`"
                aria-hidden="true"
            />
            <div class="facet-name-container">
                <span class="facet-name">{{ facetName }}</span>
                <button
                    v-if="tooltipText"
                    type="button"
                    class="info-icon-wrapper"
                    :aria-label="`Information about ${facetName}`"
                    @mouseenter="showTooltip"
                    @mouseleave="hideTooltip"
                    @focus="showTooltip"
                    @blur="hideTooltip"
                    @click.stop
                    @keydown.enter.stop
                    @keydown.space.stop.prevent
                >
                    <al-icon
                        class="info-icon"
                        icon="info"
                        aria-hidden="true"
                    />
                    <div
                        v-if="isTooltipVisible"
                        class="facet-tooltip"
                        role="tooltip"
                    >
                        {{ tooltipText }}
                    </div>
                </button>
            </div>
            <span
                v-if="selectedCount"
                class="selected-count"
                :test-id="`selectedCount-${facetName}`"
                aria-hidden="true"
            >
                {{ `(${selectedCount})` }}
            </span>
        </button>
        <div
            v-show="!isCollapsed"
            :id="`facet-body-${facetName}`"
            class="facet-body"
            :test-id="`body-${facetName}`"
            role="region"
            :aria-labelledby="`facet-header-${facetName}`"
        >
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import '@playvox/almendra/dist/components/icon';

const props = defineProps<{
  facetName: string;
  selectedCount?: number;
  collapsed?: boolean;
  tooltipText?: string;
}>();

// use prop value or default to false
const isCollapsed = ref(props.collapsed ?? false);
const isTooltipVisible = ref(false);

const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value;
};

const collapseSection = () => {
    if (!isCollapsed.value) {
        isCollapsed.value = true;
    }
};

const showTooltip = () => {
    isTooltipVisible.value = true;
};

const hideTooltip = () => {
    isTooltipVisible.value = false;
};
</script>

<style scoped>
.collapsible-facet {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-, 4px);
}

.facet-header {
  display: flex;
  width: 100%;
  min-width: 172px;
  height: 24px;
  padding: 0;
  margin: 0;
  align-items: center;
  gap: 8px;
  font-family: Roboto;
  font-weight: 500;
  font-style: normal;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #535859;
  opacity: 1;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
}

.facet-header:focus-visible {
  outline: 2px solid #101010;
  outline-offset: 2px;
  border-radius: 4px;
}

.facet-header:hover {
  color: #1A2021;
}

.facet-name-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.facet-name {
  margin: 0;
  padding: 0;
}

.chevron-icon {
  font-family: playvox-icons;
  font-weight: 400;
  font-style: normal;
  font-size: 24px;
  line-height: 24px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #1A2021;
  flex-shrink: 0;
}

.info-icon-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

.info-icon-wrapper:focus-visible {
  outline: 2px solid #101010;
  outline-offset: 2px;
  border-radius: 4px;
}

.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-family: playvox-icons;
  font-weight: 400;
  font-style: normal;
  font-size: 24px;
  line-height: 24px;
  letter-spacing: 0%;
  cursor: pointer;
  color: #1A2021;
  vertical-align: middle;
  opacity: 1;
}

.selected-count {
  flex-shrink: 0;
  margin: 0;
  padding: 0;
}

.facet-body {
  padding: 0px var(--spacing-4, 16px);
}

.facet-tooltip {
  position: absolute;
  top: 50%;
  left: calc(100% + 8px);
  transform: translateY(-50%);
  width: 355px;
  height: 90px;
  background-color: #1A2021;
  color: #FFFFFF;
  border-radius: 4px;
  padding: 16px;
  gap: 10px;
  opacity: 1;
  z-index: 1000;
  font-family: Roboto, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0%;
  vertical-align: middle;
  box-shadow: 0px 12px 8px -4px rgba(17, 66, 62, 0.1);
  white-space: normal;
  word-wrap: break-word;
  pointer-events: none;
  display: flex;
  align-items: center;
}
</style>
